import { 
  SHIPMENT_STATUS, 
  SHIPMENT_STATUS_TEXT, 
  SHIPMENT_STATUS_COLORS,
  SHIPMENT_FIELDS 
} from '../constants';

/**
 * Get status text from status code
 * @param {number} status - Status code
 * @returns {string} Status text
 */
export const getStatusText = (status) => {
  return SHIPMENT_STATUS_TEXT[status] || 'Không xác định';
};

/**
 * Get status color class from status code
 * @param {number} status - Status code
 * @returns {string} CSS class for status color
 */
export const getStatusColor = (status) => {
  return SHIPMENT_STATUS_COLORS[status] || 'bg-gray-400';
};

/**
 * Check if status can be changed to new status
 * @param {number} currentStatus - Current status
 * @param {number} newStatus - New status to change to
 * @returns {boolean} True if change is allowed
 */
export const canChangeStatus = (currentStatus, newStatus) => {
  // Cannot go back to previous status
  if (newStatus <= currentStatus) {
    return false;
  }
  
  // Cannot change if already completed
  if (currentStatus === SHIPMENT_STATUS.COMPLETED) {
    return false;
  }
  
  // Valid status transitions
  const validTransitions = {
    [SHIPMENT_STATUS.WAITING_CONFIRMATION]: [
      SHIPMENT_STATUS.ACCEPTED,
      SHIPMENT_STATUS.CANCELLED
    ],
    [SHIPMENT_STATUS.ACCEPTED]: [
      SHIPMENT_STATUS.IN_DELIVERY,
      SHIPMENT_STATUS.CANCELLED
    ],
    [SHIPMENT_STATUS.IN_DELIVERY]: [
      SHIPMENT_STATUS.COMPLETED,
      SHIPMENT_STATUS.CANCELLED
    ]
  };
  
  return validTransitions[currentStatus]?.includes(newStatus) || false;
};

/**
 * Get next available statuses for current status
 * @param {number} currentStatus - Current status
 * @returns {Array} Array of available next statuses
 */
export const getNextAvailableStatuses = (currentStatus) => {
  const transitions = {
    [SHIPMENT_STATUS.WAITING_CONFIRMATION]: [
      { value: SHIPMENT_STATUS.ACCEPTED, text: 'Nhận chuyến', color: 'bg-green-600' }
    ],
    [SHIPMENT_STATUS.ACCEPTED]: [
      { value: SHIPMENT_STATUS.IN_DELIVERY, text: 'Đang giao', color: 'bg-yellow-600' }
    ],
    [SHIPMENT_STATUS.IN_DELIVERY]: [
      { value: SHIPMENT_STATUS.COMPLETED, text: 'Hoàn thành', color: 'bg-blue-600' }
    ]
  };
  
  return transitions[currentStatus] || [];
};

/**
 * Filter shipments by date
 * @param {Array} shipments - Array of shipments
 * @param {string} dateString - Date string in DD-MM-YYYY format
 * @returns {Array} Filtered shipments
 */
export const getShipmentsForDate = (shipments, dateString) => {
  return shipments.filter(shipment => 
    shipment[SHIPMENT_FIELDS.DELIVERY_DATE] === dateString
  );
};

/**
 * Filter shipments by month and year
 * @param {Array} shipments - Array of shipments
 * @param {number} month - Month (1-12)
 * @param {number} year - Year
 * @returns {Array} Filtered shipments
 */
export const getShipmentsForMonth = (shipments, month, year) => {
  return shipments.filter(shipment => {
    const [day, shipmentMonth, shipmentYear] = shipment[SHIPMENT_FIELDS.DELIVERY_DATE].split('-');
    return parseInt(shipmentMonth) === month && parseInt(shipmentYear) === year;
  });
};

/**
 * Sort shipments by date (most recent first)
 * @param {Array} shipments - Array of shipments
 * @returns {Array} Sorted shipments
 */
export const sortShipmentsByDate = (shipments) => {
  return [...shipments].sort((a, b) => {
    const dateA = new Date(a[SHIPMENT_FIELDS.DELIVERY_DATE].split('-').reverse().join('-'));
    const dateB = new Date(b[SHIPMENT_FIELDS.DELIVERY_DATE].split('-').reverse().join('-'));
    return dateB - dateA;
  });
};

/**
 * Get shipment display info
 * @param {object} shipment - Shipment object
 * @returns {object} Display information
 */
export const getShipmentDisplayInfo = (shipment) => {
  return {
    id: shipment[SHIPMENT_FIELDS.ID],
    date: shipment[SHIPMENT_FIELDS.DELIVERY_DATE],
    status: shipment[SHIPMENT_FIELDS.STATUS],
    statusText: getStatusText(shipment[SHIPMENT_FIELDS.STATUS]),
    statusColor: getStatusColor(shipment[SHIPMENT_FIELDS.STATUS]),
    customer: shipment[SHIPMENT_FIELDS.CUSTOMER],
    pickupAddress: shipment[SHIPMENT_FIELDS.PICKUP_ADDRESS],
    deliveryAddress: shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_1],
    deliveryAddress2: shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_2],
    deliveryAddress3: shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_3],
    deliveryAddress3: shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_3],
    inputdate: shipment[SHIPMENT_FIELDS.INPUT_DATE],
    deliverydate: shipment[SHIPMENT_FIELDS.DELIVERY_DATE],
    driverName: shipment[SHIPMENT_FIELDS.DRIVER_NAME],
    vehicleNumber: shipment[SHIPMENT_FIELDS.VEHICLE_NUMBER],
    driverPhone: shipment[SHIPMENT_FIELDS.DRIVER_PHONE]
  };
};

/**
 * Check if shipment requires image upload
 * @param {number} status - Shipment status
 * @returns {boolean} True if image upload is required
 */
export const requiresImageUpload = (status) => {
  return status === SHIPMENT_STATUS.COMPLETED;
};

/**
 * Check if shipment is editable
 * @param {number} status - Shipment status
 * @returns {boolean} True if shipment can be edited
 */
export const isShipmentEditable = (status) => {
  return status !== SHIPMENT_STATUS.COMPLETED && status !== SHIPMENT_STATUS.CANCELLED;
};

/**
 * Get delivery addresses from shipment
 * @param {object} shipment - Shipment object
 * @returns {Array} Array of delivery addresses
 */
export const getDeliveryAddresses = (shipment) => {
  const addresses = [];
  
  if (shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_1]) {
    addresses.push(shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_1]);
  }
  if (shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_2]) {
    addresses.push(shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_2]);
  }
  if (shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_3]) {
    addresses.push(shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_3]);
  }
  if (shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_4]) {
    addresses.push(shipment[SHIPMENT_FIELDS.DELIVERY_ADDRESS_4]);
  }
  
  return addresses;
};

/**
 * Get customers from shipment
 * @param {object} shipment - Shipment object
 * @returns {Array} Array of customers
 */
export const getCustomers = (shipment) => {
  const customers = [];
  
  if (shipment[SHIPMENT_FIELDS.CUSTOMER]) {
    customers.push(shipment[SHIPMENT_FIELDS.CUSTOMER]);
  }
  if (shipment[SHIPMENT_FIELDS.CUSTOMER_1]) {
    customers.push(shipment[SHIPMENT_FIELDS.CUSTOMER_1]);
  }
  if (shipment[SHIPMENT_FIELDS.CUSTOMER_2]) {
    customers.push(shipment[SHIPMENT_FIELDS.CUSTOMER_2]);
  }
  if (shipment[SHIPMENT_FIELDS.CUSTOMER_3]) {
    customers.push(shipment[SHIPMENT_FIELDS.CUSTOMER_3]);
  }
  if (shipment[SHIPMENT_FIELDS.CUSTOMER_4]) {
    customers.push(shipment[SHIPMENT_FIELDS.CUSTOMER_4]);
  }
  
  return customers.filter(customer => customer && customer.trim() !== '');
};

/**
 * Validate shipment data
 * @param {object} shipmentData - Shipment data to validate
 * @returns {object} Validation result with isValid and errors
 */
export const validateShipmentData = (shipmentData) => {
  const errors = [];
  
  if (!shipmentData[SHIPMENT_FIELDS.CUSTOMER]) {
    errors.push('Khách hàng là bắt buộc');
  }
  
  if (!shipmentData[SHIPMENT_FIELDS.PICKUP_ADDRESS]) {
    errors.push('Địa chỉ lấy hàng là bắt buộc');
  }
  
  if (!shipmentData[SHIPMENT_FIELDS.DELIVERY_ADDRESS_1]) {
    errors.push('Địa chỉ giao hàng là bắt buộc');
  }
  
  if (!shipmentData[SHIPMENT_FIELDS.DELIVERY_DATE]) {
    errors.push('Ngày giao hàng là bắt buộc');
  }
  
  if (!shipmentData[SHIPMENT_FIELDS.DRIVER_NAME]) {
    errors.push('Lái xe là bắt buộc');
  }
  
  if (!shipmentData[SHIPMENT_FIELDS.VEHICLE_NUMBER]) {
    errors.push('Biển số xe là bắt buộc');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
